/**
 * Your Account Screen
 *
 * Displays read-only authentication and account information.
 * Shows sign-in method, provider metadata, and account creation date.
 * Accessible from Settings > Account > Your Account.
 */

import { useCallback, useEffect, useState } from 'react';
import {
  ActivityIndicator,
  Alert,
  ScrollView,
  StyleSheet,
} from 'react-native';
import { Stack } from 'expo-router';
import { SafeAreaView } from 'react-native-safe-area-context';

import { AuthInfoSection } from '@/components/profile/AuthInfoSection';
import { DangerZoneSection } from '@/components/profile/DangerZoneSection';
import { Colors } from '@/constants/theme';
import { Spacing } from '@/constants/spacing';
import { useColorScheme } from '@/hooks/use-color-scheme';
import { accountDeletionManager } from '@/lib/managers/AccountDeletionManager';
import { useSupabaseAuth } from '@/lib/supabase';
import { Toast } from '@/components/ui/molecules/toast';

type AuthInfo = {
  signInMethod: 'google' | 'email';
  providerName: string | null;
  providerEmail: string | null;
  accountCreatedAt: string | null;
};

export default function YourAccountScreen() {
  const colorScheme = useColorScheme();
  const palette = Colors[colorScheme ?? 'light'];
  const { session } = useSupabaseAuth();
  const [authInfo, setAuthInfo] = useState<AuthInfo | null>(null);

  const submitDeletionRequest = useCallback(async () => {
    const result = await accountDeletionManager.requestDeletion();
    if (result.success) {
      Toast.show(
        `Confirmation email sent. Link expires in ${result.expiresInMinutes} minutes.`,
        { duration: 6000 },
      );
    } else {
      Alert.alert('Could not send email', result.error);
    }
  }, []);

  const handleDeleteAccountPress = useCallback(() => {
    const email = authInfo?.providerEmail;
    if (!email) {
      Alert.alert('Account error', 'Unable to determine your account email.');
      return;
    }
    Alert.alert(
      'Delete account?',
      `We'll email a confirmation link to ${email}. Clicking it permanently deletes your FimFam account and any data we've stored about you.\n\nYour calendar events are NOT deleted — FimFam only reads from your device's native calendar accounts (Google Calendar, iCloud, etc.) and never owns those events. They'll remain in your calendar exactly as they are.`,
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Send email',
          style: 'destructive',
          onPress: () => {
            void submitDeletionRequest();
          },
        },
      ],
    );
  }, [authInfo?.providerEmail, submitDeletionRequest]);

  useEffect(() => {
    if (!session?.user) {
      setAuthInfo(null);
      return;
    }

    const user = session.user;
    const metadata = user.user_metadata || {};
    const appMetadata = user.app_metadata || {};

    let signInMethod: AuthInfo['signInMethod'] = 'email';
    if (appMetadata.provider === 'google' || appMetadata.providers?.includes('google')) {
      signInMethod = 'google';
    }

    let providerName: string | null = null;
    if (metadata.full_name && typeof metadata.full_name === 'string') {
      providerName = metadata.full_name;
    } else if (metadata.name && typeof metadata.name === 'string') {
      providerName = metadata.name;
    }

    setAuthInfo({
      signInMethod,
      providerName,
      providerEmail: user.email || null,
      accountCreatedAt: user.created_at || null,
    });
  }, [session?.user]);

  return (
    <SafeAreaView style={[styles.container, { backgroundColor: palette.background }]} edges={['bottom']}>
      <Stack.Screen
        options={{
          title: 'Your Account',
          headerShown: true,
        }}
      />

      {!authInfo ? (
        <ActivityIndicator size="large" color={palette.tint} style={styles.loader} />
      ) : (
        <ScrollView
          style={styles.scrollView}
          contentContainerStyle={styles.scrollContent}
          showsVerticalScrollIndicator={false}
        >
          <AuthInfoSection
            signInMethod={authInfo.signInMethod}
            providerName={authInfo.providerName}
            providerEmail={authInfo.providerEmail}
            accountCreatedAt={authInfo.accountCreatedAt}
            textColor={palette.text}
            backgroundColor={palette.cardBackground}
          />

          <DangerZoneSection
            textColor={palette.text}
            onDeleteAccountPress={handleDeleteAccountPress}
          />
        </ScrollView>
      )}
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  loader: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    paddingHorizontal: Spacing.lg,
    paddingVertical: Spacing.lg,
  },
});
